/**
 * TMCOMAS remanufacturing express-of-interest (EOI) structured details — v2 schema.
 * @see DPP TMCOMAS information.pptx slide 5
 */

export const SERVICE_EOI_DETAILS_SCHEMA_VERSION = 2;

export const SERVICE_EOI_ATTACHMENT_CATEGORY = {
  DRAWING: "drawing",
  PHOTO: "photo",
  NAMEPLATE_PHOTO: "nameplatePhoto",
  OVERVIEW_PHOTO: "overviewPhoto",
  DAMAGE_PHOTO: "damagePhoto",
  CONDITION_REPORT: "conditionReport",
  OTHER: "other",
};

/** Lowercase extensions without dots (slide 5: pptx, pdf, jpg, tiff, xls, …). */
export const SERVICE_EOI_ACCEPTED_FILE_EXTENSIONS = [
  "pdf",
  "jpg",
  "jpeg",
  "png",
  "tif",
  "tiff",
  "xls",
  "xlsx",
  "ppt",
  "pptx",
  "doc",
  "docx",
  "dwg",
  "dxf",
  "mov",
  "avi",
  "mp4",
];

export const SERVICE_EOI_MAX_ATTACHMENTS = 20;

/**
 * @typedef {"drawing"|"photo"|"nameplatePhoto"|"overviewPhoto"|"damagePhoto"|"conditionReport"|"other"} ServiceEoiAttachmentCategory
 */

/**
 * @typedef {Object} ServiceEoiAttachment
 * @property {string} assetRef
 * @property {string} [fileName]
 * @property {string} [mimeType]
 * @property {ServiceEoiAttachmentCategory} [category]
 */

/**
 * @typedef {Object} ServiceEoiCustomerContact
 * @property {string} [company]
 * @property {string} [plantLocation]
 * @property {string} [country]
 * @property {string} [contactName]
 * @property {string} [contactEmail]
 * @property {string} [contactPhone]
 */

/**
 * One generic component of the machine (for example a gearbox).
 * @typedef {Object} ServiceEoiMachinePart
 * @property {string} [partCategory]
 * @property {boolean} [typeKnown]
 * @property {string} [type]
 * @property {string} [serialNumber]
 * @property {boolean} [availableForRefurbishment]
 * @property {string} [currentCondition]
 * @property {number} [operatingHours]
 * @property {string} [failureDescription]
 * @property {string} [knownDamage]
 */

/**
 * @typedef {Object} ServiceEoiMachine
 * @property {string} [tagNumber]
 * @property {string} [oemBrand]
 * @property {string} [model] Platform or family when the customer uses it that way
 * @property {string} [machineCategory]
 * @property {string} [type]
 * @property {string} [serialNumber]
 * @property {ServiceEoiMachinePart} [part]
 */

/**
 * @typedef {Object} ServiceEoiServiceRequirements
 * @property {string} [requestedServiceScope]
 * @property {string} [scopeDescription]
 * @property {boolean} [accessoriesIncluded]
 * @property {string[]} [includedAccessories]
 * @property {string[]} [accessoriesRequiredAfterService]
 * @property {string} [additionalRequirements]
 */

/**
 * @typedef {Object} ServiceEoiTestingRequirements
 * @property {string} [requestedTestRun]
 * @property {string} [customerSpecificTestRequirements]
 * @property {boolean} [testReportRequired]
 */

/**
 * @typedef {Object} ServiceEoiCommercialRequirements
 * @property {boolean} [quotationRequested]
 * @property {boolean} [warrantyRequested]
 * @property {string} [warrantyRequirements]
 */

/**
 * @typedef {Object} ServiceEoiRepairScope
 * @property {string} [description]
 */

/** @type {readonly string[]} */
export const SERVICE_EOI_DIMENSION_UNITS = ["mm", "cm", "m", "in"];

/** @type {readonly string[]} */
export const SERVICE_EOI_WEIGHT_UNITS = ["kg", "g", "lb", "t"];

/**
 * @typedef {Object} ServiceEoiPartConstraints
 * @property {string} [maxLength]
 * @property {string} [maxLengthUnit]
 * @property {string} [maxWidth]
 * @property {string} [maxWidthUnit]
 * @property {string} [maxHeight]
 * @property {string} [maxHeightUnit]
 * @property {string} [maxWeight]
 * @property {string} [maxWeightUnit]
 * @property {string} [unit] Legacy shared unit; applied when per-field units are absent
 * @property {string} [drawingNotes]
 * @property {string} [toleranceNotes]
 */

/**
 * @typedef {Object} ServiceEoiRequirements
 * @property {string} [materialsAndHeatTreatment]
 * @property {string} [standardsAndCodes]
 * @property {string} [ndtRequirements]
 * @property {string} [acceptanceCriteria]
 */

/**
 * @typedef {Object} ServiceEoiDetails
 * @property {string} [assetDescription] Legacy v1; mirrored from repairScope.description when set
 * @property {string} [requestedDelivery]
 * @property {string} [customerReference]
 * @property {string} [additionalNotes]
 * @property {ServiceEoiCustomerContact} [customerContact]
 * @property {ServiceEoiMachine} [machine]
 * @property {ServiceEoiRepairScope} [repairScope]
 * @property {ServiceEoiPartConstraints} [partConstraints]
 * @property {ServiceEoiRequirements} [requirements]
 * @property {ServiceEoiServiceRequirements} [serviceRequirements]
 * @property {ServiceEoiTestingRequirements} [testingRequirements]
 * @property {ServiceEoiCommercialRequirements} [commercialRequirements]
 * @property {ServiceEoiAttachment[]} [attachments]
 */

/**
 * @param {unknown} value
 * @returns {string}
 */
function trimOptionalString(value) {
  return typeof value === "string" ? value.trim() : "";
}

/**
 * @param {unknown} value
 * @returns {boolean|undefined}
 */
function readBoolean(value) {
  if (typeof value === "boolean") return value;
  if (value === "true") return true;
  if (value === "false") return false;
  return undefined;
}

/**
 * @param {unknown} value
 * @returns {number|undefined}
 */
function readFiniteNumber(value) {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return undefined;
    const parsed = Number(trimmed);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
}

/**
 * @param {unknown} value
 * @returns {string[]}
 */
function normalizeStringList(value) {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => trimOptionalString(item))
    .filter((item) => item.length > 0);
}

/**
 * @param {unknown} raw
 * @returns {ServiceEoiAttachmentCategory|undefined}
 */
function normalizeAttachmentCategory(raw) {
  const trimmed = trimOptionalString(raw);
  if (!trimmed) return undefined;
  const match = Object.values(SERVICE_EOI_ATTACHMENT_CATEGORY).find(
    (category) => category.toLowerCase() === trimmed.toLowerCase()
  );
  return match;
}

/**
 * @param {unknown} raw
 * @returns {ServiceEoiCustomerContact|null}
 */
function normalizeCustomerContact(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return null;
  }
  const record = /** @type {Record<string, unknown>} */ (raw);
  /** @type {ServiceEoiCustomerContact} */
  const contact = {};
  const company = trimOptionalString(record.company);
  const plantLocation = trimOptionalString(
    record.plantLocation ?? record.plant_location
  );
  const country = trimOptionalString(record.country);
  const contactName = trimOptionalString(
    record.contactName ?? record.contact_name
  );
  const contactEmail = trimOptionalString(
    record.contactEmail ?? record.contact_email
  );
  const contactPhone = trimOptionalString(
    record.contactPhone ?? record.contact_phone
  );

  if (company) contact.company = company;
  if (plantLocation) contact.plantLocation = plantLocation;
  if (country) contact.country = country;
  if (contactName) contact.contactName = contactName;
  if (contactEmail) contact.contactEmail = contactEmail;
  if (contactPhone) contact.contactPhone = contactPhone;

  return Object.keys(contact).length > 0 ? contact : null;
}

/**
 * @param {unknown} raw
 * @returns {ServiceEoiMachinePart|null}
 */
function normalizeMachinePart(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return null;
  }
  const record = /** @type {Record<string, unknown>} */ (raw);
  /** @type {ServiceEoiMachinePart} */
  const part = {};
  const partCategory = trimOptionalString(
    record.partCategory ?? record.part_category
  );
  const type = trimOptionalString(record.type);
  const serialNumber = trimOptionalString(
    record.serialNumber ?? record.serial_number
  );
  const currentCondition = trimOptionalString(
    record.currentCondition ?? record.current_condition
  );
  const failureDescription = trimOptionalString(
    record.failureDescription ?? record.failure_description
  );
  const knownDamage = trimOptionalString(
    record.knownDamage ?? record.known_damage
  );
  const typeKnown = readBoolean(record.typeKnown ?? record.type_known);
  const availableForRefurbishment = readBoolean(
    record.availableForRefurbishment ?? record.available_for_refurbishment
  );
  const operatingHours = readFiniteNumber(
    record.operatingHours ?? record.operating_hours
  );

  if (partCategory) part.partCategory = partCategory;
  if (typeKnown !== undefined) part.typeKnown = typeKnown;
  if (type) part.type = type;
  if (serialNumber) part.serialNumber = serialNumber;
  if (availableForRefurbishment !== undefined) {
    part.availableForRefurbishment = availableForRefurbishment;
  }
  if (currentCondition) part.currentCondition = currentCondition;
  if (operatingHours !== undefined) part.operatingHours = operatingHours;
  if (failureDescription) part.failureDescription = failureDescription;
  if (knownDamage) part.knownDamage = knownDamage;

  return Object.keys(part).length > 0 ? part : null;
}

/**
 * @param {unknown} raw
 * @returns {ServiceEoiMachine|null}
 */
function normalizeMachine(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return null;
  }
  const record = /** @type {Record<string, unknown>} */ (raw);
  /** @type {ServiceEoiMachine} */
  const machine = {};
  const tagNumber = trimOptionalString(record.tagNumber ?? record.tag_number);
  const oemBrand = trimOptionalString(record.oemBrand ?? record.oem_brand);
  const model = trimOptionalString(record.model);
  const machineCategory = trimOptionalString(
    record.machineCategory ?? record.machine_category
  );
  const type = trimOptionalString(record.type);
  const serialNumber = trimOptionalString(
    record.serialNumber ?? record.serial_number
  );
  const part = normalizeMachinePart(record.part);

  if (tagNumber) machine.tagNumber = tagNumber;
  if (oemBrand) machine.oemBrand = oemBrand;
  if (model) machine.model = model;
  if (machineCategory) machine.machineCategory = machineCategory;
  if (type) machine.type = type;
  if (serialNumber) machine.serialNumber = serialNumber;
  if (part) machine.part = part;

  return Object.keys(machine).length > 0 ? machine : null;
}

/**
 * @param {unknown} raw
 * @returns {ServiceEoiRepairScope|null}
 */
function normalizeRepairScope(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return null;
  }
  const record = /** @type {Record<string, unknown>} */ (raw);
  const description = trimOptionalString(record.description);
  return description ? { description } : null;
}

/**
 * @param {unknown} raw
 * @returns {ServiceEoiPartConstraints|null}
 */
function normalizePartConstraints(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return null;
  }
  const record = /** @type {Record<string, unknown>} */ (raw);
  /** @type {ServiceEoiPartConstraints} */
  const constraints = {};
  const legacyUnit = trimOptionalString(record.unit);

  const scalarFields = [
    ["maxLength", "max_length"],
    ["maxWidth", "max_width"],
    ["maxHeight", "max_height"],
    ["maxWeight", "max_weight"],
    ["drawingNotes", "drawing_notes"],
    ["toleranceNotes", "tolerance_notes"],
  ];

  for (const [camel, snake] of scalarFields) {
    const value = trimOptionalString(
      record[camel] ?? (snake ? record[snake] : undefined)
    );
    if (value) {
      constraints[/** @type {keyof ServiceEoiPartConstraints} */ (camel)] =
        value;
    }
  }

  const unitFields = [
    ["maxLengthUnit", "max_length_unit"],
    ["maxWidthUnit", "max_width_unit"],
    ["maxHeightUnit", "max_height_unit"],
    ["maxWeightUnit", "max_weight_unit"],
  ];

  for (const [camel, snake] of unitFields) {
    const value = trimOptionalString(
      record[camel] ?? (snake ? record[snake] : undefined)
    );
    if (value) {
      constraints[/** @type {keyof ServiceEoiPartConstraints} */ (camel)] =
        value;
    }
  }

  if (legacyUnit) {
    constraints.unit = legacyUnit;
    if (constraints.maxLength && !constraints.maxLengthUnit) {
      constraints.maxLengthUnit = legacyUnit;
    }
    if (constraints.maxWidth && !constraints.maxWidthUnit) {
      constraints.maxWidthUnit = legacyUnit;
    }
    if (constraints.maxHeight && !constraints.maxHeightUnit) {
      constraints.maxHeightUnit = legacyUnit;
    }
    if (constraints.maxWeight && !constraints.maxWeightUnit) {
      const weightUnit = SERVICE_EOI_WEIGHT_UNITS.includes(
        /** @type {string} */ (legacyUnit)
      )
        ? legacyUnit
        : "kg";
      constraints.maxWeightUnit = weightUnit;
    }
  }

  return Object.keys(constraints).length > 0 ? constraints : null;
}

/**
 * @param {import("./serviceEoiDetails").ServiceEoiAttachment[]} all
 * @param {import("./serviceEoiDetails").ServiceEoiAttachmentCategory} category
 * @param {import("./serviceEoiDetails").ServiceEoiAttachment[]} categoryItems
 */
export function mergeServiceEoiAttachmentsByCategory(
  all,
  category,
  categoryItems
) {
  const rest = all.filter((item) => item.category !== category);
  return [
    ...rest,
    ...categoryItems.map((item) => ({
      ...item,
      category,
    })),
  ];
}

/**
 * Unit for a part-constraint field: per-field unit first, then legacy shared `unit`, then default.
 *
 * @param {import("./serviceEoiDetails").ServiceEoiPartConstraints|undefined|null} partConstraints
 * @param {string} valueKey
 * @param {string} unitKey
 * @returns {string}
 */
export function resolvePartConstraintUnit(partConstraints, valueKey, unitKey) {
  if (!partConstraints) {
    return valueKey === "maxWeight" ? "kg" : "mm";
  }

  const perField = trimOptionalString(partConstraints[unitKey]);
  if (perField) return perField;

  if (valueKey === "maxWeight") {
    const legacy = trimOptionalString(partConstraints.unit);
    if (
      legacy &&
      SERVICE_EOI_WEIGHT_UNITS.includes(/** @type {string} */ (legacy))
    ) {
      return legacy;
    }
    return "kg";
  }

  const legacy = trimOptionalString(partConstraints.unit);
  if (
    legacy &&
    SERVICE_EOI_DIMENSION_UNITS.includes(/** @type {string} */ (legacy))
  ) {
    return legacy;
  }

  return "mm";
}

/**
 * @param {import("./serviceEoiDetails").ServiceEoiPartConstraints|undefined|null} partConstraints
 * @param {string} valueKey
 * @param {string} unitKey
 */
export function formatPartConstraintDimension(
  partConstraints,
  valueKey,
  unitKey
) {
  if (!partConstraints) return "";
  const value = trimOptionalString(partConstraints[valueKey]);
  if (!value) return "";
  const unit = resolvePartConstraintUnit(partConstraints, valueKey, unitKey);
  return unit ? `${value} ${unit}` : value;
}

/**
 * @param {unknown} raw
 * @returns {ServiceEoiRequirements|null}
 */
function normalizeRequirements(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return null;
  }
  const record = /** @type {Record<string, unknown>} */ (raw);
  /** @type {ServiceEoiRequirements} */
  const requirements = {};
  const materialsAndHeatTreatment = trimOptionalString(
    record.materialsAndHeatTreatment ?? record.materials_and_heat_treatment
  );
  const standardsAndCodes = trimOptionalString(
    record.standardsAndCodes ?? record.standards_and_codes
  );
  const ndtRequirements = trimOptionalString(
    record.ndtRequirements ?? record.ndt_requirements
  );
  const acceptanceCriteria = trimOptionalString(
    record.acceptanceCriteria ?? record.acceptance_criteria
  );

  if (materialsAndHeatTreatment) {
    requirements.materialsAndHeatTreatment = materialsAndHeatTreatment;
  }
  if (standardsAndCodes) requirements.standardsAndCodes = standardsAndCodes;
  if (ndtRequirements) requirements.ndtRequirements = ndtRequirements;
  if (acceptanceCriteria) requirements.acceptanceCriteria = acceptanceCriteria;

  return Object.keys(requirements).length > 0 ? requirements : null;
}

/**
 * @param {unknown} raw
 * @returns {ServiceEoiServiceRequirements|null}
 */
function normalizeServiceRequirements(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return null;
  }
  const record = /** @type {Record<string, unknown>} */ (raw);
  /** @type {ServiceEoiServiceRequirements} */
  const requirements = {};
  const requestedServiceScope = trimOptionalString(
    record.requestedServiceScope ?? record.requested_service_scope
  );
  const scopeDescription = trimOptionalString(
    record.scopeDescription ?? record.scope_description
  );
  const additionalRequirements = trimOptionalString(
    record.additionalRequirements ?? record.additional_requirements
  );
  const accessoriesIncluded = readBoolean(
    record.accessoriesIncluded ?? record.accessories_included
  );
  const includedAccessories = normalizeStringList(
    record.includedAccessories ?? record.included_accessories
  );
  const accessoriesRequiredAfterService = normalizeStringList(
    record.accessoriesRequiredAfterService ??
      record.accessories_required_after_service
  );

  if (requestedServiceScope) {
    requirements.requestedServiceScope = requestedServiceScope;
  }
  if (scopeDescription) requirements.scopeDescription = scopeDescription;
  if (accessoriesIncluded !== undefined) {
    requirements.accessoriesIncluded = accessoriesIncluded;
  }
  if (includedAccessories.length > 0) {
    requirements.includedAccessories = includedAccessories;
  }
  if (accessoriesRequiredAfterService.length > 0) {
    requirements.accessoriesRequiredAfterService =
      accessoriesRequiredAfterService;
  }
  if (additionalRequirements) {
    requirements.additionalRequirements = additionalRequirements;
  }

  return Object.keys(requirements).length > 0 ? requirements : null;
}

/**
 * @param {unknown} raw
 * @returns {ServiceEoiTestingRequirements|null}
 */
function normalizeTestingRequirements(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return null;
  }
  const record = /** @type {Record<string, unknown>} */ (raw);
  /** @type {ServiceEoiTestingRequirements} */
  const requirements = {};
  const requestedTestRun = trimOptionalString(
    record.requestedTestRun ?? record.requested_test_run
  );
  const customerSpecificTestRequirements = trimOptionalString(
    record.customerSpecificTestRequirements ??
      record.customer_specific_test_requirements
  );
  const testReportRequired = readBoolean(
    record.testReportRequired ?? record.test_report_required
  );

  if (requestedTestRun) requirements.requestedTestRun = requestedTestRun;
  if (customerSpecificTestRequirements) {
    requirements.customerSpecificTestRequirements =
      customerSpecificTestRequirements;
  }
  if (testReportRequired !== undefined) {
    requirements.testReportRequired = testReportRequired;
  }

  return Object.keys(requirements).length > 0 ? requirements : null;
}

/**
 * @param {unknown} raw
 * @returns {ServiceEoiCommercialRequirements|null}
 */
function normalizeCommercialRequirements(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return null;
  }
  const record = /** @type {Record<string, unknown>} */ (raw);
  /** @type {ServiceEoiCommercialRequirements} */
  const requirements = {};
  const quotationRequested = readBoolean(
    record.quotationRequested ?? record.quotation_requested
  );
  const warrantyRequested = readBoolean(
    record.warrantyRequested ?? record.warranty_requested
  );
  const warrantyRequirements = trimOptionalString(
    record.warrantyRequirements ?? record.warranty_requirements
  );

  if (quotationRequested !== undefined) {
    requirements.quotationRequested = quotationRequested;
  }
  if (warrantyRequested !== undefined) {
    requirements.warrantyRequested = warrantyRequested;
  }
  if (warrantyRequirements) {
    requirements.warrantyRequirements = warrantyRequirements;
  }

  return Object.keys(requirements).length > 0 ? requirements : null;
}

/**
 * @param {unknown} raw
 * @returns {ServiceEoiAttachment[]}
 */
function normalizeAttachments(raw) {
  if (!Array.isArray(raw)) return [];

  /** @type {ServiceEoiAttachment[]} */
  const attachments = [];

  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const record = /** @type {Record<string, unknown>} */ (item);
    const assetRef = trimOptionalString(record.assetRef ?? record.asset_ref);
    if (!assetRef) continue;

    /** @type {ServiceEoiAttachment} */
    const attachment = { assetRef };
    const fileName = trimOptionalString(record.fileName ?? record.file_name);
    const mimeType = trimOptionalString(record.mimeType ?? record.mime_type);
    const category = normalizeAttachmentCategory(record.category);

    if (fileName) attachment.fileName = fileName;
    if (mimeType) attachment.mimeType = mimeType;
    if (category) attachment.category = category;

    attachments.push(attachment);
    if (attachments.length >= SERVICE_EOI_MAX_ATTACHMENTS) break;
  }

  return attachments;
}

/**
 * Primary repair description for validation and legacy `assetDescription`.
 * @param {ServiceEoiDetails|null|undefined} eoi
 */
export function getServiceEoiRepairDescription(eoi) {
  if (!eoi) return "";
  return (
    trimOptionalString(eoi.repairScope?.description) ||
    trimOptionalString(eoi.assetDescription)
  );
}

/**
 * @param {ServiceEoiDetails|null|undefined} eoi
 */
export function hasServiceEoiDisplayContent(eoi) {
  if (!eoi || typeof eoi !== "object") return false;

  if (getServiceEoiRepairDescription(eoi)) return true;
  if (eoi.requestedDelivery || eoi.customerReference || eoi.additionalNotes) {
    return true;
  }
  if (eoi.customerContact && Object.keys(eoi.customerContact).length > 0) {
    return true;
  }
  if (eoi.machine && Object.keys(eoi.machine).length > 0) return true;
  if (eoi.partConstraints && Object.keys(eoi.partConstraints).length > 0) {
    return true;
  }
  if (eoi.requirements && Object.keys(eoi.requirements).length > 0) {
    return true;
  }
  if (
    eoi.serviceRequirements &&
    Object.keys(eoi.serviceRequirements).length > 0
  ) {
    return true;
  }
  if (
    eoi.testingRequirements &&
    Object.keys(eoi.testingRequirements).length > 0
  ) {
    return true;
  }
  if (
    eoi.commercialRequirements &&
    Object.keys(eoi.commercialRequirements).length > 0
  ) {
    return true;
  }
  if (Array.isArray(eoi.attachments) && eoi.attachments.length > 0) return true;

  return false;
}

/**
 * @param {unknown} eoiDetails
 * @returns {ServiceEoiDetails|null}
 */
export function normalizeServiceEoiDetails(eoiDetails) {
  if (
    !eoiDetails ||
    typeof eoiDetails !== "object" ||
    Array.isArray(eoiDetails)
  ) {
    return null;
  }

  const record = /** @type {Record<string, unknown>} */ (eoiDetails);
  /** @type {ServiceEoiDetails} */
  const normalized = {};

  const repairScope = normalizeRepairScope(
    record.repairScope ?? record.repair_scope
  );
  const legacyAssetDescription = trimOptionalString(
    record.assetDescription ?? record.asset_description
  );
  const repairDescription =
    repairScope?.description || legacyAssetDescription || "";

  if (repairDescription) {
    normalized.repairScope = { description: repairDescription };
    normalized.assetDescription = repairDescription;
  }

  const requestedDelivery = trimOptionalString(
    record.requestedDelivery ?? record.requested_delivery
  );
  const customerReference = trimOptionalString(
    record.customerReference ?? record.customer_reference
  );
  const additionalNotes = trimOptionalString(
    record.additionalNotes ?? record.additional_notes
  );

  if (requestedDelivery) normalized.requestedDelivery = requestedDelivery;
  if (customerReference) normalized.customerReference = customerReference;
  if (additionalNotes) normalized.additionalNotes = additionalNotes;

  const customerContact = normalizeCustomerContact(
    record.customerContact ?? record.customer_contact
  );
  const machine = normalizeMachine(record.machine);
  const partConstraints = normalizePartConstraints(
    record.partConstraints ?? record.part_constraints
  );
  const requirements = normalizeRequirements(record.requirements);
  const serviceRequirements = normalizeServiceRequirements(
    record.serviceRequirements ?? record.service_requirements
  );
  const testingRequirements = normalizeTestingRequirements(
    record.testingRequirements ?? record.testing_requirements
  );
  const commercialRequirements = normalizeCommercialRequirements(
    record.commercialRequirements ?? record.commercial_requirements
  );
  const attachments = normalizeAttachments(record.attachments);

  if (customerContact) normalized.customerContact = customerContact;
  if (machine) normalized.machine = machine;
  if (partConstraints) normalized.partConstraints = partConstraints;
  if (requirements) normalized.requirements = requirements;
  if (serviceRequirements) normalized.serviceRequirements = serviceRequirements;
  if (testingRequirements) normalized.testingRequirements = testingRequirements;
  if (commercialRequirements) {
    normalized.commercialRequirements = commercialRequirements;
  }
  if (attachments.length > 0) normalized.attachments = attachments;

  return hasServiceEoiDisplayContent(normalized) ? normalized : null;
}

/**
 * @param {unknown} eoiDetails
 * @returns {{ valid: boolean, errorKey?: string }}
 */
export function validateServiceEoiDetailsForSubmit(eoiDetails) {
  const normalized = normalizeServiceEoiDetails(eoiDetails);
  if (!getServiceEoiRepairDescription(normalized)) {
    return { valid: false, errorKey: "serviceEoiRepairDescriptionRequired" };
  }
  return { valid: true };
}

/**
 * @param {string} fileName
 */
export function isServiceEoiAcceptedFileName(fileName) {
  const name = String(fileName ?? "").toLowerCase();
  const dot = name.lastIndexOf(".");
  if (dot < 0) return false;
  const ext = name.slice(dot + 1);
  return SERVICE_EOI_ACCEPTED_FILE_EXTENSIONS.includes(ext);
}

/**
 * MIME accept map for react-dropzone.
 */
export function serviceEoiDropzoneAccept() {
  return {
    "application/pdf": [".pdf"],
    "image/jpeg": [".jpg", ".jpeg"],
    "image/png": [".png"],
    "image/tiff": [".tif", ".tiff"],
    "application/vnd.ms-excel": [".xls"],
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
      ".xlsx",
    ],
    "application/vnd.ms-powerpoint": [".ppt"],
    "application/vnd.openxmlformats-officedocument.presentationml.presentation":
      [".pptx"],
    "application/msword": [".doc"],
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
      ".docx",
    ],
    "video/mp4": [".mp4"],
    "video/quicktime": [".mov"],
    "video/x-msvideo": [".avi"],
  };
}
